import { IControl } from "sonobello.utilities.react";

import { CustomDimension } from "../../../../Models/CustomDimension";
import { LeadResult } from "../../../../Models/Lead";

/** A convenience class which provides an easy representation of the 'not offered procedure' options available for
 * selection by the user and their current selection state.
 */
class NotOfferedProcedureOptions {
  /** The available not offered procedure options and their current selection state. */
  readonly state: (CustomDimension<string> & IControl<boolean>)[];
  /** The origination list of not offered procedure options. */
  private options: CustomDimension<string>[];

  constructor(
    options: CustomDimension<string>[],
    prior: (CustomDimension<string> & IControl<boolean>)[],
    change?: CustomDimension<string> & IControl<boolean>
  ) {
    this.options = options;
    this.state = [...prior];
    if (change) {
      const index = this.state.findIndex(o => o.id === change.id);
      if (index === -1) throw `Failed to find not offered procedure '${change.name}'.`;
      this.state[index].value = change.value;
    }
  }

  /** Toggles the selection value for the specified option. */
  toggleOption = (change: CustomDimension<string> & IControl<boolean>): NotOfferedProcedureOptions =>
    new NotOfferedProcedureOptions(this.options, this.state, { ...change, value: !change.value });

  /** Transforms this object into the format expected by a lead result. */
  getForLeadResult = (): LeadResult["notOfferedProcedures"] =>
    this.state.reduce((obj, { id, value }) => {
      obj[id] = value;
      return obj;
    }, {} as Record<string, boolean>);

  /** Build this object from the format available from a lead result. */
  static fromLeadResult = (
    procedures: LeadResult["notOfferedProcedures"],
    options: CustomDimension<string>[]
  ): NotOfferedProcedureOptions =>
    new NotOfferedProcedureOptions(
      options,
      options
        .sort((o1, o2) => (o1.name > o2.name ? 1 : -1))
        .map(option => ({ ...option, value: procedures[option.id] ?? false }))
    );
}

export default NotOfferedProcedureOptions;
